import { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useMapsConfig } from '../../contexts/MapsContext';
import { GoogleMap, MarkerF, InfoWindowF, Circle as CircleG, useJsApiLoader } from '@react-google-maps/api';

// Fix default leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const darkBlueSvg = encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41"><path d="M12.5 0C5.6 0 0 5.6 0 12.5c0 2.4.7 4.7 1.9 6.6L12.5 41l10.6-21.9c1.2-1.9 1.9-4.2 1.9-6.6C25 5.6 19.4 0 12.5 0z" fill="#1a3a6c"/><circle cx="12.5" cy="12.5" r="5" fill="white"/></svg>');

function getLeafletIcon(color) {
  if (color === 'darkblue') {
    return new L.Icon({
      iconUrl: 'data:image/svg+xml,' + darkBlueSvg,
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
      iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
    });
  }
  return new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-' + (color || 'blue') + '.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
  });
}

const darkBluePinSvg = encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="36" height="48" viewBox="0 0 36 48"><path d="M18 0C8.1 0 0 8.1 0 18c0 3.5 1 6.8 2.8 9.6L18 48l15.2-20.4C35 24.8 36 21.5 36 18 36 8.1 27.9 0 18 0z" fill="#1a3a6c"/><circle cx="18" cy="18" r="7" fill="white"/></svg>');
const googleMarkerIcons = {
  red: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
  blue: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
  green: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png',
  darkblue: 'data:image/svg+xml,' + darkBluePinSvg,
};

function ClickHandler(props) {
  useMapEvents({ 
    click: function(e) { 
      if (props.onClick) {
        props.onClick(e.latlng.lat, e.latlng.lng); 
      }
    } 
  });
  return null;
}

function MapResizer() {
  const map = useMap();
  
  useEffect(function() {
    function invalidate() {
      if (map) {
        map.invalidateSize();
      }
    }
    
    invalidate();
    var t1 = setTimeout(invalidate, 100);
    var t2 = setTimeout(invalidate, 300);
    var t3 = setTimeout(invalidate, 500);
    var t4 = setTimeout(invalidate, 1000);
    
    function handleVisibility() {
      if (document.visibilityState === 'visible') {
        setTimeout(invalidate, 100);
      }
    }
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('resize', invalidate);
    
    return function() {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('resize', invalidate);
    };
  }, [map]);
  
  return null;
}

function Recenterer(props) {
  const map = useMap();
  useEffect(function() {
    if (props.center && props.center[0] && props.center[1]) {
      map.setView(props.center, map.getZoom());
      setTimeout(function() { map.invalidateSize(); }, 50);
    }
  }, [props.center, map]);
  return null;
}

function LeafletMap(props) {
  var center = props.center;
  var zoom = props.zoom;
  var markers = props.markers;
  var circles = props.circles;
  var onClick = props.onClick;
  
  var mapKey = useState(0)[0];
  var setMapKey = useState(0)[1];
  
  useEffect(function() {
    var isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    var isStandalone = window.navigator.standalone === true;
    
    if (isIOS && isStandalone) {
      var timer = setTimeout(function() { setMapKey(function(k) { return k + 1; }); }, 100);
      return function() { clearTimeout(timer); };
    }
  }, [setMapKey]);

  return (
    <MapContainer 
      key={mapKey}
      center={center} 
      zoom={zoom || 13} 
      style={{ height: '100%', width: '100%', minHeight: '200px' }} 
      zoomControl={false}
      whenReady={function(mapInstance) {
        setTimeout(function() {
          mapInstance.target.invalidateSize();
        }, 100);
      }}
    >
      <TileLayer 
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" 
        attribution="&copy; OpenStreetMap"
      />
      {onClick && <ClickHandler onClick={onClick} />}
      <Recenterer center={center} />
      <MapResizer />
      {markers && markers.map(function(m, i) {
        return (
          <Marker key={m.id || `marker-${m.lat}-${m.lng}`} position={[m.lat, m.lng]} icon={getLeafletIcon(m.color)}>
            {m.label && <Popup>{m.label}</Popup>}
          </Marker>
        );
      })}
      {circles && circles.map(function(c, i) {
        return (
          <Circle key={c.id || `circle-${c.lat}-${c.lng}`} center={[c.lat, c.lng]} radius={c.radius} pathOptions={{ color: '#f59e0b', fillColor: '#fbbf24', fillOpacity: 0.15 }} />
        );
      })}
    </MapContainer>
  );
}

function GoogleMapInner(props) {
  var center = props.center;
  var zoom = props.zoom;
  var markers = props.markers;
  var circles = props.circles;
  var onClick = props.onClick;
  var apiKey = props.apiKey;
  var onError = props.onError;
  
  var mapErrorState = useState(false);
  var mapError = mapErrorState[0];
  var setMapError = mapErrorState[1];
  
  var loaderResult = useJsApiLoader({ 
    googleMapsApiKey: apiKey,
    id: 'google-map-script',
    libraries: ['places']  // Include Places library for address autocomplete
  });
  var isLoaded = loaderResult.isLoaded;
  var loadError = loaderResult.loadError;
  
  var selectedMarkerState = useState(null);
  var selectedMarker = selectedMarkerState[0];
  var setSelectedMarker = selectedMarkerState[1];

  useEffect(function() {
    if (loadError) {
      console.error('[GoogleMapInner] Google Maps failed to load:', loadError.message || loadError);
      setMapError(true);
      if (onError) {
        onError();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadError, onError]);

  // If Google Maps failed, signal parent to use fallback
  if (mapError) {
    console.warn('[GoogleMapInner] Returning null due to map error, fallback should engage');
    return null;
  }

  if (!isLoaded) {
    return <div className="bg-stone-100 rounded-xl animate-pulse w-full h-full flex items-center justify-center text-sm text-stone-500">Loading Google Maps...</div>;
  }

  console.log('[GoogleMapInner] Google Maps loaded successfully');

  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '100%' }}
      center={{ lat: center[0], lng: center[1] }}
      zoom={zoom || 13}
      onClick={onClick ? function(e) { onClick(e.latLng.lat(), e.latLng.lng()); } : undefined}
      options={{ 
        zoomControl: true, 
        streetViewControl: false, 
        mapTypeControl: false, 
        fullscreenControl: false,
        gestureHandling: 'greedy',
        disableDefaultUI: false
      }}
      onLoad={function(map) {
        setTimeout(function() {
          if (window.google && window.google.maps) {
            window.google.maps.event.trigger(map, 'resize');
          }
        }, 100);
      }}
    >
      {markers && markers.map(function(m, i) {
        const markerId = m.id || `gmarker-${m.lat}-${m.lng}`;
        return (
          <MarkerF
            key={markerId}
            position={{ lat: m.lat, lng: m.lng }}
            icon={googleMarkerIcons[m.color] || undefined}
            onClick={function() { if (m.label) setSelectedMarker(markerId); }}
          >
            {selectedMarker === markerId && m.label && (
              <InfoWindowF onCloseClick={function() { setSelectedMarker(null); }}>
                <div className="text-sm">{m.label}</div>
              </InfoWindowF>
            )}
          </MarkerF>
        );
      })}
      {circles && circles.map(function(c, i) {
        return (
          <CircleG key={c.id || `gcircle-${c.lat}-${c.lng}`} center={{ lat: c.lat, lng: c.lng }} radius={c.radius} options={{ strokeColor: '#f59e0b', fillColor: '#fbbf24', fillOpacity: 0.15 }} />
        );
      })}
    </GoogleMap>
  );
}

export default function UnifiedMap(props) {
  var center = props.center;
  var zoom = props.zoom;
  var markers = props.markers;
  var circles = props.circles;
  var onClick = props.onClick;
  var height = props.height || '250px';
  var className = props.className || '';
  
  var mapsConfig = useMapsConfig();
  var provider = mapsConfig.provider;
  var apiKey = mapsConfig.apiKey;
  
  var googleFailedState = useState(false);
  var googleFailed = googleFailedState[0];
  var setGoogleFailed = googleFailedState[1];
  
  // Use Google Maps if configured (removed iOS standalone restriction)
  var useGoogleMaps = provider === 'google' && apiKey && !googleFailed;

  // Debug logging
  console.log('[UnifiedMap] Config:', { provider, hasApiKey: !!apiKey, googleFailed, useGoogleMaps });

  function handleGoogleError() {
    setGoogleFailed(true);
  }

  // Map container style with isolation
  var containerStyle = {
    height: typeof height === 'number' ? height + 'px' : height,
    minHeight: '200px',
    position: 'relative',
    WebkitOverflowScrolling: 'touch',
    overflow: 'hidden',
    isolation: 'isolate',  // Creates new stacking context
    zIndex: 0  // Keep map at base level
  };

  return (
    <div 
      className={'rounded-xl overflow-hidden border border-stone-200 ' + className}
      style={containerStyle} 
      data-testid="unified-map"
      data-map-provider={useGoogleMaps ? 'google' : 'leaflet'}
    >
      {/* Map wrapper to contain Google Maps z-index */}
      <div style={{ position: 'relative', width: '100%', height: '100%', zIndex: 1 }}>
        {useGoogleMaps ? (
          <GoogleMapInner 
            center={center} 
            zoom={zoom} 
            markers={markers} 
            circles={circles} 
            onClick={onClick} 
            apiKey={apiKey}
            onError={handleGoogleError}
          />
        ) : (
          <LeafletMap 
            center={center} 
            zoom={zoom} 
            markers={markers} 
            circles={circles} 
            onClick={onClick}
          />
        )}
      </div>
    </div>
  );
}
